import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { RouterOutputs } from "@alm/api-contracts";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import { searchPatch, optionalSearch } from "@/lib/search";
import { useFacility } from "@/lib/facility/facility";
import { orpc, orpcClient } from "@/lib/orpc";
import { guardRoute } from "@/lib/policy/route-guard";
import { DirectoryPage, type DirectoryColumn } from "./-components/directory-page";
import { useReferralSourceColumn } from "./-components/referral-source-column";
import { facilityInput } from "./-components/directory-forms";
import { facilityValue } from "@/routes/_authenticated/_care/-components/directory-forms-shared";
import { keepWhileSame } from "@/lib/query/keep-while-same";
import {
  PharmacyForm,
  type PharmacyValues,
} from "@/routes/_authenticated/_care/-components/pharmacy-form";

export const Route = createFileRoute("/_authenticated/_care/pharmacies")({
  validateSearch: (search: Record<string, unknown>): { q?: string } => ({
    q: optionalSearch(search.q),
  }),
  beforeLoad: ({ context }) => guardRoute(context.me, "pharmacies"),
  component: PharmaciesPage,
});

type Row = RouterOutputs["care"]["directories"]["pharmacies"]["list"][number];

const COLUMNS: ReadonlyArray<DirectoryColumn<Row>> = [
  {
    header: "Pharmacy",
    cell: (row) => <span className="font-medium">{row.name}</span>,
    clamp: "md",
  },
  { header: "Phone", cell: (row) => row.phone ?? "-" },
  /* Fax is not a relic here: it is how a refill goes to a pharmacy, so a pharmacy whose fax number
   * is not on the screen is one nobody can send to without going and looking it up. Both columns
   * were already coming back from the API; nothing was rendering them. */
  { header: "Fax", cell: (row) => row.fax ?? "-" },
  { header: "Email", cell: (row) => row.email ?? "-", clamp: "md", lines: 1 },
  {
    header: "Location",
    cell: (row) => [row.city, row.state].filter(Boolean).join(", ") || "-",
    clamp: "md",
  },
  {
    header: "Hours",
    cell: (row) => (row.is24Hour ? "24 hours" : (row.hours ?? "-")),
    clamp: "sm",
  },
  { header: "Delivers", cell: (row) => (row.deliveryAvailable ? "Yes" : "No") },
];

function PharmaciesPage() {
  const { facilityId } = useFacility();
  const { q } = Route.useSearch();
  const patch = searchPatch(useNavigate({ from: Route.fullPath }));
  const {
    text: search,
    change: setSearch,
    settling,
  } = useSearchParamText(q, (next) => patch({ q: next }));
  const list = useQuery({
    ...orpc.care.directories.pharmacies.list.queryOptions({
      input: { facilityId, search: q || undefined, includeInactive: true },
    }),
    placeholderData: keepWhileSame({ facilityId }),
  });
  const referral = useReferralSourceColumn("pharmacy", list.data);
  const columns = referral ? [...COLUMNS, referral] : COLUMNS;
  return (
    <DirectoryPage<Row, PharmacyValues>
      noun="Pharmacy"
      plural="pharmacies"
      title="Pharmacy directory"
      meta="Where this building's medication comes from."
      columns={columns}
      rows={list.data}
      isPending={list.isPending}
      error={list.error}
      search={search}
      onSearch={setSearch}
      searching={settling || list.isPlaceholderData}
      create={(values) =>
        orpcClient.care.directories.pharmacies.create({
          ...values,
          facilityId: facilityInput(values.facilityId),
        })
      }
      update={(row, values) =>
        orpcClient.care.directories.pharmacies.update({ pharmacyId: row.id, ...values })
      }
      remove={(row) => orpcClient.care.directories.pharmacies.remove({ pharmacyId: row.id })}
      toValues={(row) => ({
        ...row,
        facilityId: facilityValue(row.facilityId),
        deliveryAvailable: row.deliveryAvailable ?? false,
        is24Hour: row.is24Hour ?? false,
      })}
      renderForm={(args) => <PharmacyForm {...args} />}
    />
  );
}
